import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { client } from "../../utils/supabase";

// Modal para overridear manualmente la fecha de cobro estimada de una entry del Backlog.
// Soporta tres orígenes:
//   - "sales": columna directa sales.fecha_cobro_estimada (por id).
//   - "contratos": key dentro del objeto de cuota en el JSONB sales_contratos.meses[cuota_index].
//   - "carteleria" HARDWARE (tramo del split, con entry_index): key aditiva en el JSONB
//     negocios_carteleria.detalle_pagos_hardware[entry_index].fecha_cobro_estimada (Fase 2B).
//     CAJA PURA: escribe SOLO esa key; no toca facturafecha/facturanumero/monto/porcentaje ni el
//     resto del array. La cuota de GESTIÓN de cartelería (sin entry_index) NO se edita acá.

// Default del tramo de hardware = facturafecha + 30 (espejo de DIAS_COBRO_HARDWARE_CAPEX en
// pipelineCobroSII.js — misma regla; el pipeline es la fuente de verdad del default, acá solo se
// muestra como referencia "fecha automática").
const DIAS_COBRO_HARDWARE_CAPEX = 30;

const addDias = (fechaStr, dias) => {
  if (!fechaStr) return null;
  const d = new Date(`${String(fechaStr).substring(0, 10)}T12:00:00`);
  d.setDate(d.getDate() + dias);
  return d.toISOString().split("T")[0];
};

const parseDetalle = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v !== "string") return null;
  try {
    const p = JSON.parse(v || "[]");
    return Array.isArray(p) ? p : null;
  } catch {
    return null;
  }
};

const formatFechaLarga = (fechaStr) => {
  if (!fechaStr) return "—";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [yyyy, mm, dd] = f.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

const formatMonto = (entry) => {
  const n = Number(entry?.importe || 0);
  const num = n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  return entry?.moneda_dolar ? `US$ ${num}` : num;
};

const EditarFechaCobroModal = ({ show, onHide, entry, onSaved }) => {
  // Tramo de hardware de cartelería: origen "carteleria" + entry_index (lo pone la Fase 2A).
  const esHardwareCarteleria =
    entry?.origen === "carteleria" && entry?.entry_index != null && entry?.negocio_id != null;

  const [fecha, setFecha] = useState(entry?.fecha_override_actual || "");
  const [guardando, setGuardando] = useState(false);
  // El pipeline no desglosa la fecha de factura ni el override crudo del tramo → se leen del jsonb
  // al abrir, para precargar el input y calcular la "fecha automática" (facturafecha + 30).
  const [hwFacturaFecha, setHwFacturaFecha] = useState(null);
  const [hwOverrideActual, setHwOverrideActual] = useState(null);

  useEffect(() => {
    if (!show || !esHardwareCarteleria) return undefined;
    let vivo = true;
    (async () => {
      const { data, error } = await client
        .from("negocios_carteleria")
        .select("detalle_pagos_hardware")
        .eq("id", entry.negocio_id)
        .single();
      if (!vivo) return;
      if (error) {
        // 10.177: no se traga el error; el usuario lo ve. El guardado re-lee y valida igual.
        toast.error(`No se pudo leer el tramo de hardware: ${error.message}`);
        return;
      }
      const detalle = parseDetalle(data?.detalle_pagos_hardware) || [];
      const tramo = detalle[entry.entry_index] || null;
      setHwFacturaFecha(tramo?.facturafecha || null);
      const override = tramo?.fecha_cobro_estimada || null;
      setHwOverrideActual(override);
      setFecha(override || "");
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!entry) return null;

  const esContrato = entry.origen === "contratos";
  const fechaAutomatica = esHardwareCarteleria
    ? addDias(hwFacturaFecha, DIAS_COBRO_HARDWARE_CAPEX)
    : entry.fecha_automatica;
  const tieneOverride = esHardwareCarteleria ? hwOverrideActual != null : entry.tiene_override;

  const persistir = async (nuevaFecha) => {
    if (esHardwareCarteleria) {
      // Caja pura: read-modify-write del jsonb escribiendo SOLO la key aditiva fecha_cobro_estimada
      // en ESE índice. No se reordena (venta_ref_index es posicional) ni se toca ninguna otra key
      // (facturafecha/facturanumero/monto/porcentaje) ni otra entry. Update por id (text, 10.7).
      const { data, error: errLeer } = await client
        .from("negocios_carteleria")
        .select("detalle_pagos_hardware")
        .eq("id", entry.negocio_id)
        .single();
      if (errLeer) throw errLeer;
      const detalle = parseDetalle(data?.detalle_pagos_hardware);
      if (!detalle || entry.entry_index >= detalle.length || !detalle[entry.entry_index]) {
        // 10.177: no escribir a ciegas si el tramo ya no existe (p.ej. cambió el split).
        throw new Error("El tramo de hardware ya no existe en el detalle (¿cambió el split?)");
      }
      const nuevo = [...detalle];
      const tramo = { ...nuevo[entry.entry_index] };
      if (nuevaFecha) tramo.fecha_cobro_estimada = nuevaFecha;
      else delete tramo.fecha_cobro_estimada;
      nuevo[entry.entry_index] = tramo;
      const { error } = await client
        .from("negocios_carteleria")
        .update({ detalle_pagos_hardware: nuevo })
        .eq("id", entry.negocio_id);
      if (error) throw error;
      return;
    }
    if (entry.origen === "sales") {
      const { error } = await client.from("sales").update({ fecha_cobro_estimada: nuevaFecha }).eq("id", entry.sale_id);
      if (error) throw error;
      return;
    }
    if (entry.origen === "contratos") {
      const { data, error: errLeer } = await client
        .from("sales_contratos")
        .select("meses")
        .eq("id", entry.contrato_id)
        .single();
      if (errLeer) throw errLeer;
      const mesesActualizado = [...(data?.meses || [])];
      const cuota = { ...mesesActualizado[entry.cuota_index] };
      if (nuevaFecha) {
        cuota.fecha_cobro_estimada = nuevaFecha;
      } else {
        delete cuota.fecha_cobro_estimada;
      }
      mesesActualizado[entry.cuota_index] = cuota;
      const { error } = await client
        .from("sales_contratos")
        .update({ meses: mesesActualizado })
        .eq("id", entry.contrato_id);
      if (error) throw error;
      return;
    }
    throw new Error(`Origen no soportado: ${entry.origen}`);
  };

  const handleGuardar = async () => {
    if (!fecha) {
      toast.error("Seleccioná una fecha");
      return;
    }
    setGuardando(true);
    try {
      await persistir(fecha);
      toast.success("Fecha de cobro actualizada");
      // Patch puntual (Fase 2A): el padre reubica la fila del Backlog sin refetch. Pasa la fecha
      // efectiva nueva + el override para que reconstruya el item de `pipeline` por su id.
      onSaved?.({ fecha_estimada: fecha, fecha_override_actual: fecha, tiene_override: true });
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  const handleVolverAutomatica = async () => {
    setGuardando(true);
    try {
      await persistir(null);
      toast.success("Restaurada a fecha automática");
      // Revert: la fecha efectiva vuelve a la automática (el modal la conoce en todos los orígenes,
      // incluido hardware de cartelería que la deriva de facturafecha+30). override = false.
      onSaved?.({ fecha_estimada: fechaAutomatica, fecha_override_actual: null, tiene_override: false });
    } catch (e) {
      toast.error(`Error al restaurar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Editar fecha de cobro estimada</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 16 }}>
          <div>
            <strong>Cliente:</strong> {entry.cliente || "—"}
          </div>
          <div>
            <strong>Monto:</strong> {formatMonto(entry)}
          </div>
          {esContrato && (
            <div>
              <strong>Cuota:</strong> {entry.cuota_index + 1}
            </div>
          )}
          <div>
            <strong>Fecha automática:</strong> {formatFechaLarga(fechaAutomatica)}
            <span style={{ color: "#888", marginLeft: 6, fontSize: "0.8rem" }}>(sin override)</span>
          </div>
        </div>

        <Form.Group>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Fecha de cobro override</Form.Label>
          <Form.Control type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} disabled={guardando} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <div>
          {tieneOverride && (
            <Button variant="outline-secondary" size="sm" onClick={handleVolverAutomatica} disabled={guardando}>
              Volver a automática
            </Button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
            Cerrar
          </Button>
          <Button variant="primary" size="sm" onClick={handleGuardar} disabled={guardando}>
            {guardando ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EditarFechaCobroModal;
